import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Search, Bell, Menu, Send, Award, Loader2, X, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { BACKEND_URL } from '../../config/canonical';
import StatusPill from '../StatusPill';

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  return now;
}
const pad = (n) => String(n).padStart(2, '0');

const ADMIN_ROLES = new Set(['main_hub', 'department_admin', 'training_director', 'super_admin']);

const apiCall = async (path, opts = {}) => {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('nexus_token')}`,
      ...(opts.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
  return data;
};

export function NxCommandBar({ user, onSearch, onToggleSidebar }) {
  const now = useClock();
  const navigate = useNavigate();
  const token = typeof window !== 'undefined' ? localStorage.getItem('nexus_token') : null;
  const isAdmin = ADMIN_ROLES.has(user?.role);
  const [unread, setUnread] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [pendingCerts, setPendingCerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const panelRef = useRef(null);

  const fetchAll = useCallback(async () => {
    if (!token) return;
    try {
      const n = await apiCall('/api/notifications').catch(() => ({ notifications: [], unread_count: 0 }));
      setNotifs(n.notifications || []);
      setUnread(n.unread_count || 0);
      if (isAdmin) {
        const p = await apiCall('/api/training-center/admin/certificates/pending').catch(() => ({ pending: [] }));
        setPendingCerts(p.pending || []);
      }
    } catch (err) { /* silent */ }
  }, [token, isAdmin]);

  useEffect(() => {
    fetchAll();
    const t = setInterval(fetchAll, 45000);
    return () => clearInterval(t);
  }, [fetchAll]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const totalAlerts = unread + (isAdmin ? pendingCerts.length : 0);

  const issueCert = async (cert) => {
    setBusyId(cert.id);
    try {
      await apiCall(`/api/training-center/admin/certificates/${cert.id}/issue`, {
        method: 'POST',
        body: JSON.stringify({
          user_name: cert.user_name,
          cert_title: cert.cert_title,
          notify_user: true,
        }),
      });
      toast.success(`Issued · ${cert.user_name}`);
      fetchAll();
    } catch (e) {
      toast.error(`Issue failed: ${e.message}`);
    } finally { setBusyId(null); }
  };

  const markRead = async (notif) => {
    try {
      await apiCall(`/api/notifications/${notif.id}/read`, { method: 'POST' });
      fetchAll();
    } catch (e) { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await apiCall(`/api/notifications/read-all`, { method: 'POST' });
      fetchAll();
    } catch (e) { /* silent */ }
  };

  const openNotif = (n) => {
    markRead(n);
    if (n.link) navigate(n.link);
    setOpen(false);
  };

  const stamp = `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: '2-digit' });

  return (
    <header className="nx-tb" data-testid="nx-tb">
      <button
        type="button"
        className="nx-btn nx-btn--icon"
        onClick={onToggleSidebar}
        aria-label="Open module menu"
        data-testid="nx-tb-menu"
      >
        <Menu size={18} />
      </button>

      <div className="nx-tb-status flex" data-testid="nx-tb-on-duty">
        {user?.role && user.role !== 'client' ? (
          <StatusPill />
        ) : (
          <>
            <span className="nx-pulse" />
            <span>ON DUTY</span>
          </>
        )}
      </div>

      <div className="hidden lg:flex items-center gap-3" data-testid="nx-tb-clock" style={{ color: 'var(--nx-ink-soft)' }}>
        <span className="nx-mono" style={{ fontSize: 12, fontWeight: 700 }}>{dateStr}</span>
        <span className="nx-mono" style={{ fontSize: 14, fontWeight: 700, color: 'var(--nx-blue-hot)' }}>{stamp}</span>
      </div>

      <div className="flex-1 max-w-2xl mx-auto w-full" data-testid="nx-tb-search">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const q = (e.currentTarget.elements['nx-global-search'].value || '').trim();
            if (!q) return;
            onSearch?.(q);
            navigate(`/cases?q=${encodeURIComponent(q)}`);
          }}
        >
          <label className="nx-search" htmlFor="nx-global-search">
            <Search size={16} style={{ color: 'var(--nx-ink-dim)' }} />
            <input
              id="nx-global-search"
              name="nx-global-search"
              data-testid="nx-global-search"
              placeholder="Search cases, subjects, applicants, evidence… (press Enter)"
              onChange={(e) => onSearch?.(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const q = (e.currentTarget.value || '').trim();
                  if (q) navigate(`/cases?q=${encodeURIComponent(q)}`);
                }
              }}
            />
            <span className="nx-mono" style={{ fontSize: 11, color: 'var(--nx-ink-faint)', fontWeight: 600 }}>↵</span>
          </label>
        </form>
      </div>

      <div className="flex items-center gap-2 relative" ref={panelRef}>
        <button
          type="button"
          className="nx-btn nx-btn--icon relative"
          aria-label="Notifications"
          data-testid="nx-tb-bell"
          onClick={() => setOpen(o => !o)}
        >
          <Bell size={16} />
          {totalAlerts > 0 && (
            <span
              data-testid="nx-tb-bell-count"
              style={{
                position: 'absolute', top: -4, right: -4,
                background: 'var(--nx-red)', color: 'white',
                fontSize: 10, fontWeight: 800, padding: '2px 6px',
                borderRadius: '999px', minWidth: 18, textAlign: 'center',
                boxShadow: '0 0 0 2px var(--nx-base-deep)',
              }}>
              {totalAlerts > 9 ? '9+' : totalAlerts}
            </span>
          )}
        </button>

        {/* ===== Live Pings dropdown ===== */}
        {open && (
          <>
            {/* Mobile backdrop */}
            <div
              className="fixed inset-0 bg-slate-950/70 backdrop-blur-sm z-[75] sm:hidden"
              onClick={() => setOpen(false)}
            />
            <div
              data-testid="nx-bell-dropdown"
              className="fixed sm:absolute right-0 sm:top-full top-14 sm:mt-2 mx-2 sm:mx-0 w-[calc(100vw-1rem)] sm:w-[420px] max-h-[80vh] overflow-hidden rounded-lg shadow-2xl z-[80]"
              style={{
                background: '#0E1626',
                border: '1px solid var(--nx-line)',
              }}
            >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/60">
              <div>
                <p className="text-sm font-bold text-white">Live Pings</p>
                <p className="text-[10px] font-mono uppercase tracking-wider text-slate-400">
                  {totalAlerts} alert{totalAlerts !== 1 ? 's' : ''}
                </p>
              </div>
              <div className="flex items-center gap-1">
                {unread > 0 && (
                  <button
                    onClick={markAllRead}
                    className="text-[10px] font-mono uppercase tracking-wider text-slate-400 hover:text-amber-300 px-2 py-1"
                    data-testid="nx-bell-mark-all"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="text-slate-400 hover:text-white p-1"
                  aria-label="Close"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 60px)' }}>
              {/* Admin: Pending certificates 1-click queue */}
              {isAdmin && pendingCerts.length > 0 && (
                <div className="border-b border-slate-700/40">
                  <div className="px-4 py-2 bg-amber-500/10 flex items-center gap-1.5">
                    <Award className="w-3.5 h-3.5 text-amber-300" />
                    <p className="text-[10px] font-mono uppercase tracking-wider text-amber-300 font-bold">
                      Certificates awaiting issuance · {pendingCerts.length}
                    </p>
                  </div>
                  {pendingCerts.slice(0, 6).map(cert => (
                    <div
                      key={cert.id}
                      data-testid={`bell-pending-${cert.id}`}
                      className="px-4 py-2.5 border-b border-slate-700/30 flex items-center justify-between gap-2 hover:bg-slate-800/40"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-white truncate">{cert.user_name}</p>
                        <p className="text-[11px] text-slate-400 truncate">{cert.cert_title}</p>
                      </div>
                      <button
                        onClick={() => issueCert(cert)}
                        disabled={busyId === cert.id}
                        data-testid={`bell-issue-${cert.id}`}
                        className="text-[11px] font-semibold px-2.5 py-1.5 rounded bg-amber-500 hover:bg-amber-400 text-slate-900 inline-flex items-center gap-1 disabled:opacity-50 flex-shrink-0"
                      >
                        {busyId === cert.id ? (
                          <><Loader2 className="w-3 h-3 animate-spin" /> Issuing…</>
                        ) : (
                          <><Send className="w-3 h-3" /> Issue</>
                        )}
                      </button>
                    </div>
                  ))}
                  {pendingCerts.length > 6 && (
                    <button
                      onClick={() => { navigate('/admin/certifications'); setOpen(false); }}
                      data-testid="bell-view-all-pending"
                      className="w-full px-4 py-2 text-[11px] font-mono uppercase tracking-wider text-amber-300 hover:bg-amber-500/10"
                    >
                      View all {pendingCerts.length} →
                    </button>
                  )}
                </div>
              )}

              {/* Notifications list */}
              {notifs.length === 0 && pendingCerts.length === 0 ? (
                <div className="px-4 py-10 text-center">
                  <Bell className="w-8 h-8 mx-auto text-slate-600 mb-2" />
                  <p className="text-xs text-slate-400">All clear · No alerts</p>
                </div>
              ) : (
                <div data-testid="bell-notif-list">
                  {notifs.slice(0, 12).map(n => (
                    <button
                      key={n.id}
                      onClick={() => openNotif(n)}
                      data-testid={`bell-notif-${n.id}`}
                      className={`w-full text-left px-4 py-2.5 border-b border-slate-700/30 hover:bg-slate-800/40 flex items-start gap-2 ${
                        n.read ? 'opacity-60' : ''
                      }`}
                    >
                      {!n.read && (
                        <span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-amber-400 flex-shrink-0" />
                      )}
                      <div className={`min-w-0 flex-1 ${n.read ? 'pl-3' : ''}`}>
                        <p className="text-xs font-semibold text-white truncate">{n.title}</p>
                        <p className="text-[11px] text-slate-400 line-clamp-2">{n.body}</p>
                        <p className="text-[10px] font-mono text-slate-500 mt-0.5">
                          {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            </div>
          </>
        )}

        <div className="hidden md:flex items-center gap-2 px-3 py-1.5"
             data-testid="nx-tb-user"
             style={{ background: 'var(--nx-surface)', border: '1px solid var(--nx-line)', borderRadius: 10 }}>
          <div style={{
            width: 30, height: 30,
            background: 'linear-gradient(135deg, var(--nx-blue), var(--nx-blue-deep))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'white', fontFamily: 'var(--nx-font-display)', fontWeight: 800, fontSize: 13,
            borderRadius: 8,
          }}>
            {(user?.name || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="leading-tight">
            <div style={{ fontSize: 13, fontWeight: 700, color: 'white' }}>{user?.name || 'Operator'}</div>
            <div className="nx-mono" style={{ fontSize: 10, color: 'var(--nx-ink-dim)', letterSpacing: '0.10em', textTransform: 'uppercase', fontWeight: 600 }}>
              {(user?.role || 'operator').replace(/_/g, ' ')}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
